import { useCallback, useState } from 'react';
import { shelfView, toggleValue } from '../shelf';

/**
 * Filtering a shelf of books by subject and class.
 *
 * <p>Shared by the Content Library's catalogue and the student's own library, which
 * are the same question asked of two lists — the admin wants the Maths books because
 * they are loading them, the student wants them because that is the homework. The
 * arithmetic behind it is `shelfView` in src/shelf.js, which is pure and has a harness;
 * this file is the controls.
 */

/**
 * Holds the two selections and hands back everything both callers need.
 *
 * <p>A hook rather than state inside {@link ShelfFilters}, because the filtered list is
 * what the page renders and the component that owns the chips cannot hand it upwards.
 */
/**
 * The shortest shelf worth filtering. Below it the control is taller than the grid
 * under it and every chip reads (1).
 */
const MINIMUM = 5;

export function useShelfFilter(items) {
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);

  const clearAll = useCallback(() => {
    setSubjects([]);
    setClasses([]);
  }, []);

  const shelf = shelfView(items, { subjects, classes });

  return {
    shelf,
    clearAll,
    /** Spread straight onto {@link ShelfFilters}. */
    filterProps: {
      shelf,
      onToggleSubject: (value) => setSubjects((current) => toggleValue(current, value)),
      onToggleClass: (value) => setClasses((current) => toggleValue(current, value)),
      onClearSubjects: () => setSubjects([]),
      onClearClasses: () => setClasses([]),
      onClearAll: clearAll,
    },
  };
}

/**
 * The shelf's two facets, as rows of chips.
 *
 * <p>Chips rather than two dropdowns. A dropdown hides what the options are until it
 * is opened and shows one choice at a time; a catalogue is browsed by looking, and
 * "Maths and Science, classes 6 to 8" is a thing somebody wants in one go. The counts
 * are on the chips because the useful question before clicking is how much is behind
 * it, and a value with nothing behind it says (0) and cannot be pressed — it stays in
 * the row rather than vanishing, since a chip that disappears as you filter is one you
 * cannot untick.
 *
 * <p>Absent entirely when there is nothing to choose between — one subject and one
 * class is not a filter, it is two buttons that do nothing — and absent on a shelf
 * short enough that the chips would be longer than the books they hide. The student
 * side is what that second rule is for: a catalogue is forty books and a library is
 * four, and over four books a filter is furniture.
 */
export default function ShelfFilters({
  shelf,
  onToggleSubject,
  onToggleClass,
  onClearSubjects,
  onClearClasses,
  onClearAll,
}) {
  const choices = Math.max(shelf.subjects.length, shelf.classes.length);
  if (choices < 2 || shelf.total < MINIMUM) return null;

  return (
    <section className="card card-pad" style={{ marginBottom: 'var(--s2)' }}>
      <div className="facets">
        {shelf.subjects.length > 1 && (
          <FacetRow
            label="Subject"
            options={shelf.subjects}
            onToggle={onToggleSubject}
            onClear={onClearSubjects}
          />
        )}
        {shelf.classes.length > 1 && (
          <FacetRow
            label="Class"
            options={shelf.classes}
            onToggle={onToggleClass}
            onClear={onClearClasses}
          />
        )}
      </div>

      <div className="row-between" style={{ marginTop: 'var(--s1)' }}>
        {/* Live, because the count changes without the focus moving: a chip press
            updates a number several rows down and a screen reader should hear it. */}
        <span className="small muted" role="status">
          {shelf.filtered
            ? `${shelf.matches.length} of ${shelf.total} ${shelf.total === 1 ? 'book' : 'books'}`
            : `${shelf.total} ${shelf.total === 1 ? 'book' : 'books'}`}
        </span>
        {shelf.filtered && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClearAll}>
            Clear filters
          </button>
        )}
      </div>
    </section>
  );
}

function FacetRow({ label, options, onToggle, onClear }) {
  const anyPicked = options.some((option) => option.selected);
  return (
    <div className="facet" role="group" aria-label={`Filter by ${label.toLowerCase()}`}>
      <span className="facet-label">{label}</span>
      <div className="filter-chips">
        {/* "All" is the absence of a pick rather than a value of its own, so it is
            pressed exactly when nothing else is and clears rather than selects. */}
        <button
          type="button"
          className="chip"
          aria-pressed={!anyPicked}
          onClick={onClear}
        >
          All
        </button>
        {options.map((option) => (
          <button
            type="button"
            key={option.value}
            className="chip"
            aria-pressed={option.selected}
            disabled={option.count === 0 && !option.selected}
            onClick={() => onToggle(option.value)}
          >
            {/* Plain text rather than a visual count plus a hidden sentence: a
                screen reader reads "Mathematics 4" from this, which is the same
                thing the chip says to everyone else. */}
            {option.value} ({option.count})
          </button>
        ))}
      </div>
    </div>
  );
}
